export const GROMMET_LABELS = {
    top_bottom: "Top & Bottom",
    all_sides: "All Sides",
    four_corners: "4 Corners",
    every_2_3_ft: "Every 2–3 ft",
    every_1_2_ft: "Every 1–2 ft",
};

const GROMMET_SPACING = { top_bottom: 24, all_sides: 24, every_2_3_ft: 30, every_1_2_ft: 18 };

function spread(lengthIn, spacingIn) {
    const n = Math.max(2, Math.round(lengthIn / spacingIn) + 1);
    return Array.from({ length: n }, (_, i) => i / (n - 1));
}

// Returns grommet positions as fractions of the sign face {x, y}
export function grommetPositions(preset, wIn, hIn) {
    if (!preset || !wIn || !hIn) return [];
    if (preset === "four_corners") return [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }];
    const spacing = GROMMET_SPACING[preset] || 24;
    const xs = spread(wIn, spacing);
    const pts = xs.flatMap((x) => [{ x, y: 0 }, { x, y: 1 }]);
    if (preset === "top_bottom") return pts;
    const ys = spread(hIn, spacing).filter((y) => y > 0 && y < 1);
    return [...pts, ...ys.flatMap((y) => [{ x: 0, y }, { x: 1, y }])];
}

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export function isBoolOption(opt) {
    return !(opt.choices && opt.choices.length) && opt.id !== "grommets";
}

// Human readable chip value for the sticky summary bar
export function chipValue(opt, value) {
    if (opt.id === "grommets") return value ? GROMMET_LABELS[value] || value : "NO";
    if (isBoolOption(opt)) return value ? "YES" : "NO";
    const choice = (opt.choices || []).find((c) => c.id === value);
    return choice ? choice.label : "PICK";
}

export function roundedCornerInches(value) {
    if (!value || value === "none") return 0;
    const map = { 1: 1, "3/4\"": 0.75, "1/2\"": 0.5, "1/4\"": 0.25, "1-2": 0.5, "1": 1 };
    if (map[value] != null) return map[value];
    const m = String(value).match(/([\d.]+)\s*\/\s*([\d.]+)/);
    if (m) return Number(m[1]) / Number(m[2]);
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
}

export function isGlossy(options) {
    return Boolean(
        options.gloss_finish || options.gloss_laminate ||
        options.laminate === "gloss" || options.laminate === "gloss_laminate" ||
        ["gloss_laminate", "gloss_front", "gloss_double"].includes(options.coating)
    );
}

export function sizeLabel(w, h) {
    const f = (n) => (Number(n) % 1 === 0 ? Number(n) : Number(n).toFixed(2).replace(/0$/, ""));
    return `${f(w || 0)}" × ${f(h || 0)}"`;
}

const RATE_UNITS = { sqft: "/sqft", sheet: "/sheet", bundle: "/bundle", each: " each" };
/** " · $4/sheet" — the rate and what it is per, for the stage caption. */
export const rateCaption = (q) => (q?.tier_price && RATE_UNITS[q.rate_unit] ? ` · $${q.tier_price}${RATE_UNITS[q.rate_unit]}` : "");

/** The small print under the price: sheets and pieces for cards, cards for bundles, sqft for everything else. */
export function priceNoteOf(model, q) {
    if (!q) return "";
    const each = `${money(q.unit_price)} each`;
    if (model === "per_sheet_bulk") {
        const sheets = q.sheets_needed ? `${q.sheets_needed} ${q.sheets_needed === 1 ? "sheet" : "sheets"}` : null;
        return [sheets, q.pieces_printed ? `${q.pieces_printed} printed` : null, each].filter(Boolean).join(" · ");
    }
    if (model === "per_bundle") return [q.sheets_needed ? `${q.sheets_needed} cards` : null, `${money(q.unit_price)} a bundle`].filter(Boolean).join(" · ");
    return [q.area_sqft ? `${q.area_sqft} sqft` : null, each].filter(Boolean).join(" · ");
}

/** Landscape is wider than tall, portrait the other way round. Swaps the two when they disagree. */
export function orient(w, h, orientation) {
    const a = Number(w) || 0;
    const b = Number(h) || 0;
    if (!orientation || !a || !b || a === b) return [w, h];
    const wide = a > b;
    if ((orientation === "portrait" && wide) || (orientation === "landscape" && !wide)) return [h, w];
    return [w, h];
}

/* ---------------------------------------------------------------------------------------- */
/* The designer's option rules. The catalog stores what a product offers; this says how each  */
/* option behaves on screen — a two-way switch, a list, what it starts at, what it needs off.  */
/* ---------------------------------------------------------------------------------------- */

const YES_NO = [{ id: "yes", label: "Yes" }, { id: "no", label: "No" }];
export const SIDE_LABELS = {
    none: "None", top: "Top only", bottom: "Bottom only", left: "Left only", right: "Right only",
    top_bottom: "Top & Bottom", left_right: "Left & Right", all_sides: "All sides",
};
const sides = (...ids) => ids.map((id) => ({ id, label: SIDE_LABELS[id] }));
export const GROMMET_SIDE_CHOICES = sides("top_bottom", "all_sides", "top", "bottom", "left_right", "left", "right");
export const GROMMET_SPACING_CHOICES = [
    { id: "every_2_3_ft", label: "Every 2–3 ft" }, { id: "every_1_2_ft", label: "Every 1–2 ft" }, { id: "corners", label: "Corners only" },
];
const SPACING_IN = { every_2_3_ft: 30, every_1_2_ft: 18 };

/** Is a yes/no or side option switched on? Accepts the old true/false values too. */
export const optionOn = (v) => v === true || v === "yes" || (typeof v === "string" && v && !["no", "none", ""].includes(v) && !v.startsWith("no_"));

const on = (o, id) => optionOn(o[id]);
/** Only one of a pair can be on at a time. */
const oneOf = (otherId, otherName, thisName) => (o) => (on(o, otherId) ? `Pick one: turn off ${otherName} to add ${thisName}.` : null);
const CUT_NOTE = "Custom cut and contour cut are two ways of cutting the same sign — pick one.";

export const OPTION_RULES = {
    // banners
    welding: { choices: YES_NO, initial: "yes", help: "The edge is folded over and heat-welded so it does not fray." },
    grommets: { choices: YES_NO, initial: "no", help: "Metal rings you hang the sign from. Pick where they go under the sign." },
    rope: {
        choices: sides("none", "bottom", "top", "top_bottom"), initial: "none",
        help: "A rope sewn into the edge.",
        blockedBy: (o) => (on(o, "grommets") ? "To add rope, remove the grommets first." : null),
    },
    pole_pocket: {
        choices: sides("none", "top", "bottom", "top_bottom", "left", "right", "left_right"), initial: "none",
        help: "A sleeve sewn along the edge so a pole slides through it.",
        blockedBy: (o) => (o.welding === "yes" ? "To add a pole pocket, remove welding first." : null),
    },
    wind_slits: { choices: YES_NO, initial: "no", help: "Half-moon cuts that let wind through so the banner does not tear." },
    webbing: { choices: YES_NO, initial: "no" },
    color_matching: { choices: YES_NO, initial: "no", help: "We match a Pantone or brand color before we print." },

    // rigid signs
    step_stakes_standard: {
        choices: YES_NO, initial: "no", help: "Wire H-stakes that push into the lawn. One pair per sign.",
        blockedBy: oneOf("step_stakes_hd", "the heavy-duty stakes", "standard stakes"),
    },
    step_stakes_hd: {
        choices: YES_NO, initial: "no", help: "Thicker stakes for wind and hard ground. One pair per sign.",
        blockedBy: oneOf("step_stakes_standard", "the standard stakes", "heavy-duty stakes"),
    },
    custom_cut: {
        choices: YES_NO, initial: "no", help: "Cut to a shape you give us (circle, arrow, your outline).",
        blockedBy: (o) => (on(o, "contour_cut") ? CUT_NOTE : null),
    },
    contour_cut: {
        choices: YES_NO, initial: "no", help: "Cut along the edge of your art. Put the cut line in your file or tell us in the order notes.",
        blockedBy: (o) => (on(o, "custom_cut") ? CUT_NOTE : null),
    },
    rounded_corners: {
        initial: "none", help: "Corners rounded to this radius.",
        blockedBy: (o) => (on(o, "contour_cut") || on(o, "custom_cut") ? "A sign cut to a shape has no corners to round." : null),
    },
    gloss_finish: { choices: YES_NO, initial: "no", help: "A shiny clear coat over the print." },
    score_and_fold: {
        choices: YES_NO, initial: "no", help: "We score the board so it folds — for tent signs and table toppers.",
        blockedBy: (o, ctx) => (ctx?.variant?.print_sides === "double" ? "Score and fold is for single-sided boards — switch to single-sided first." : null),
    },
    flute_direction: { help: "Which way the ribs inside the board run. Stakes slide into vertical flutes." },
    standoff_silver: {
        choices: YES_NO, initial: "no", help: "Metal posts that float the sign off the wall.",
        blockedBy: oneOf("standoff_black", "the black stand-offs", "silver ones"),
    },
    standoff_black: {
        choices: YES_NO, initial: "no", help: "Metal posts that float the sign off the wall.",
        blockedBy: oneOf("standoff_silver", "the silver stand-offs", "black ones"),
    },

    // adhesive vinyl
    laminate: { initial: "no_laminate", help: "A clear top layer that protects the print from sun and scratches." },
    gloss_laminate: { choices: YES_NO, initial: "no", help: "A clear gloss top layer that protects the print." },
    application: { help: "Inside: stuck to the inside of the glass. Outside: stuck on the outside." },
    viewable: { help: "Which side people read it from. Inside glass, read from outside = we print it in reverse so it reads right." },

    // handheld cards
    orientation: { help: "Landscape is wider than it is tall, portrait is taller than it is wide. The artboard flips to match." },
    print_sides_opt: { help: "Double-sided opens a second window for your back image and a flip-over button on the artboard." },
    coating: {
        help: "A clear gloss layer over the print.",
        disabledChoice: (id, o) => (id === "gloss_double" && o.print_sides_opt !== "double-sided" ? "double-sided only" : null),
        fixValue: (v, o) => (v === "gloss_double" && o.print_sides_opt !== "double-sided" ? "gloss_front" : v),
    },
};

const isYesNo = (choices) => choices.length === 2 && choices.every((c) => ["yes", "no"].includes(c.id));

/** What one option looks like on screen: its choices, the value it starts with and when it is off-limits. */
export function optionRule(opt) {
    const rule = OPTION_RULES[opt.id];
    const choices = rule?.choices || (opt.choices?.length ? opt.choices : YES_NO);
    const initial = rule?.initial ?? (isYesNo(choices) ? "no" : choices[0].id);
    return {
        choices, initial, help: rule?.help, blockedBy: rule?.blockedBy || (() => null), yesNo: isYesNo(choices),
        disabledChoice: rule?.disabledChoice || (() => null), fixValue: rule?.fixValue || ((v) => v),
    };
}

/** Every option at its starting value, so nothing on the sign says "pick". */
export function defaultOptions(product) {
    const out = {};
    (product?.options || []).forEach((opt) => { out[opt.id] = optionRule(opt).initial; });
    if ("grommets" in out) { out.grommet_sides = "top_bottom"; out.grommet_spacing = "every_2_3_ft"; }
    return out;
}

/** A window cling stuck inside the glass but read from the street is printed mirrored. */
export const isMirrorPrint = (o) => o.application === "inside" && o.viewable === "outside";

/** The label for the value an option currently holds. */
export function optionLabel(opt, value) {
    const { choices } = optionRule(opt);
    if (value === true) return "Yes";
    if (value === false || value == null || value === "") return choices.find((c) => c.id === "no" || c.id === "none")?.label || "No";
    return choices.find((c) => c.id === value)?.label || String(value);
}

const EDGES = {
    top_bottom: ["top", "bottom"], all_sides: ["top", "bottom", "left", "right"], left_right: ["left", "right"],
    top: ["top"], bottom: ["bottom"], left: ["left"], right: ["right"],
};
export const edgesOf = (value) => EDGES[value] || [];

/** Where the grommets sit, as fractions of the face. Corners come free with every edge. */
export function grommetPoints(sidesValue, spacing, wIn, hIn) {
    const edges = edgesOf(sidesValue);
    if (!edges.length || !wIn || !hIn) return [];
    const inset = { x: Math.min(1 / wIn, 0.25), y: Math.min(1 / hIn, 0.25) };
    const along = (len) => {
        const gap = SPACING_IN[spacing];
        const n = gap ? Math.max(2, Math.round(len / gap) + 1) : 2;
        return Array.from({ length: n }, (_, i) => i / (n - 1));
    };
    const pts = new Map();
    const put = (x, y) => pts.set(`${x.toFixed(4)},${y.toFixed(4)}`, { x: inset.x + x * (1 - 2 * inset.x), y: inset.y + y * (1 - 2 * inset.y) });
    edges.forEach((e) => {
        if (e === "top" || e === "bottom") along(wIn).forEach((x) => put(x, e === "top" ? 0 : 1));
        else along(hIn).forEach((y) => put(e === "left" ? 0 : 1, y));
    });
    return [...pts.values()];
}

/** One line per option for the cart and the shop's ticket, skipping everything that is off. */
export function optionSummary(product, options) {
    const lines = (product.options || []).flatMap((opt) => {
        const v = options[opt.id];
        if (!optionOn(v)) return [];
        if (opt.id === "grommets") {
            const sp = GROMMET_SPACING_CHOICES.find((c) => c.id === options.grommet_spacing)?.label || "";
            return [`Grommets: ${SIDE_LABELS[options.grommet_sides] || "Top & Bottom"}${sp ? ` · ${sp.toLowerCase()}` : ""}`];
        }
        return [`${opt.label}: ${optionLabel(opt, v)}`];
    });
    if (isMirrorPrint(options)) lines.push("Printed in reverse (reads right through the glass)");
    return lines;
}

/** Material and print-sides split out of a product's variants, when the variants carry both. */
export function variantAxes(variants = []) {
    const split = variants.length > 1 && variants.every((v) => v.material && v.print_sides);
    if (!split) return { split: false, materials: [], sidesFor: () => [] };
    const materials = [...new Set(variants.map((v) => v.material))];
    const sidesFor = (material) => variants.filter((v) => v.material === material).map((v) => v.print_sides);
    return { split: true, materials, sidesFor };
}
